/**
 * Utility functions for managing the view tree structure
 */

/**
 * Generate a unique ID for a view node
 */
export const generateViewId = (type) => {
  return `${type.toLowerCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get default modifiers structure
 */
export const getDefaultModifiers = () => {
  return {
    padding: {
      all: null,
      horizontal: null,
      vertical: null,
      top: null,
      bottom: null,
      leading: null,
      trailing: null,
    },
    frame: {
      width: null,
      height: null,
      minWidth: null,
      maxWidth: null,
      minHeight: null,
      maxHeight: null,
      idealWidth: null,
      idealHeight: null,
      maxWidthInfinity: false,
      maxHeightInfinity: false,
    },
    background: {
      type: 'none', // 'none' | 'color' | 'gradient'
      color: '#FFFFFF',
      gradientColors: ['#F26F29', '#F03224'],
    },
    foregroundColor: null,
    opacity: 1.0,
    cornerRadius: 0,
    shadow: {
      enabled: false,
      radius: 8,
      opacity: 0.2,
      x: 0,
      y: 4,
    },
    border: {
      enabled: false,
      width: 1,
      color: '#CCCCCC',
    },
    rotation: 0,
    scale: {
      x: 1.0,
      y: 1.0,
    },
    offset: {
      x: 0,
      y: 0,
    },
    clipShape: 'none', // 'none' | 'circle' | 'roundedRectangle' | 'capsule' | 'ellipse'
    aspectRatio: {
      enabled: false,
      ratio: 1.0,
      contentMode: 'fit', // 'fit' | 'fill'
    },
  };
};

/**
 * Get default properties for a view type
 */
export const getDefaultProperties = (type) => {
  const baseModifiers = getDefaultModifiers();
  
  switch (type) {
    case 'HStack':
    case 'VStack':
      return { 
        spacing: 8, 
        alignment: 'center',
        modifiers: baseModifiers,
      };
    case 'ZStack':
      return { 
        alignment: 'center',
        modifiers: baseModifiers,
      };
    case 'Text':
      return { 
        content: 'Text', 
        fontSize: 16, 
        fontWeight: 'regular', 
        color: '#000000', 
        alignment: 'leading',
        modifiers: baseModifiers,
      };
    case 'Button':
      return { 
        title: 'Button', 
        style: 'bordered',
        modifiers: baseModifiers,
      };
    case 'Image':
      return { 
        source: 'star.fill', 
        size: 24, 
        aspectRatio: 'fit', 
        resizable: true,
        modifiers: baseModifiers,
      };
    case 'Spacer':
      return { 
        minLength: 0,
        modifiers: baseModifiers,
      };
    case 'Divider':
      return { 
        color: '#CCCCCC', 
        thickness: 1,
        modifiers: baseModifiers,
      };
    case 'List':
    case 'ScrollView':
      return {
        modifiers: baseModifiers,
      };
    default:
      return {
        modifiers: baseModifiers,
      };
  }
};

/**
 * Find a node in the tree by ID
 */
export const findNodeById = (nodeId, tree) => {
  for (const node of tree) {
    if (node.id === nodeId) {
      return { node, parent: null, index: tree.indexOf(node) };
    }
    const found = findNodeInChildren(nodeId, node);
    if (found) {
      return found;
    }
  }
  return null;
};

/**
 * Helper to find node in children recursively
 */
const findNodeInChildren = (nodeId, parentNode) => {
  for (let i = 0; i < parentNode.children.length; i++) {
    const child = parentNode.children[i];
    if (child.id === nodeId) {
      return { node: child, parent: parentNode, index: i };
    }
    const found = findNodeInChildren(nodeId, child);
    if (found) {
      return found;
    }
  }
  return null;
};

/**
 * Add a new view node to the tree
 */
export const addViewNode = (parentId, type, tree) => {
  const newNode = {
    id: generateViewId(type),
    type,
    properties: getDefaultProperties(type),
    children: [],
  };

  if (!parentId) {
    // Add as root node
    return [...tree, newNode];
  }

  const findAndAdd = (nodes) => {
    return nodes.map(node => {
      if (node.id === parentId) {
        return {
          ...node,
          children: [...node.children, newNode],
        };
      }
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: findAndAdd(node.children),
        };
      }
      return node;
    });
  };

  return findAndAdd(tree);
};

/**
 * Remove a view node from the tree
 */
export const removeViewNode = (nodeId, tree) => {
  const removeFromNodes = (nodes) => {
    return nodes
      .filter(node => node.id !== nodeId)
      .map(node => ({
        ...node,
        children: removeFromNodes(node.children || []),
      }));
  };

  return removeFromNodes(tree);
};

/**
 * Update view properties
 */
export const updateViewProperties = (nodeId, properties, tree) => {
  const updateInNodes = (nodes) => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          properties: { ...node.properties, ...properties },
        };
      }
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: updateInNodes(node.children),
        };
      }
      return node;
    });
  };

  return updateInNodes(tree);
};

/**
 * Check if moving a node to a new parent would create a circular reference
 */
export const isValidMove = (nodeId, newParentId, tree) => {
  if (!newParentId || nodeId === newParentId) {
    return true; // Moving to root or same node (which would be filtered out anyway)
  }

  // Check if newParentId is a descendant of nodeId (would create cycle)
  const isDescendant = (parentId, checkId, nodes) => {
    for (const node of nodes) {
      if (node.id === parentId) {
        // Check children recursively
        const checkDescendant = (childId, children) => {
          for (const child of children || []) {
            if (child.id === childId) {
              return true;
            }
            if (child.children && child.children.length > 0) {
              if (checkDescendant(childId, child.children)) {
                return true;
              }
            }
          }
          return false;
        };
        return checkDescendant(checkId, node.children || []);
      }
      if (node.children && node.children.length > 0) {
        const found = isDescendant(parentId, checkId, node.children);
        if (found !== undefined) {
          return found;
        }
      }
    }
    return false;
  };

  return !isDescendant(nodeId, newParentId, tree);
};

/**
 * Move/reorder a view node
 * @param {string} nodeId - ID of node to move
 * @param {string|null} newParentId - ID of new parent (null for root)
 * @param {number} newIndex - Index position in new parent's children array
 * @param {Array} tree - Current view tree
 * @returns {Array} Updated view tree
 */
export const moveViewNode = (nodeId, newParentId, newIndex, tree) => {
  // Validate the move
  if (!isValidMove(nodeId, newParentId, tree)) {
    console.warn('Invalid move: would create circular reference');
    return tree;
  }

  // Find current location of node
  const findNodeLocation = (nodes, targetId, parentId = null, path = []) => {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].id === targetId) {
        return { node: nodes[i], parentId, index: i, path };
      }
      if (nodes[i].children && nodes[i].children.length > 0) {
        const found = findNodeLocation(nodes[i].children, targetId, nodes[i].id, [...path, i]);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const location = findNodeLocation(tree, nodeId);
  if (!location) {
    return tree; // Node not found
  }

  const { node: nodeToMove, parentId: oldParentId, index: oldIndex } = location;

  // If moving to same parent and same position, no change needed
  if (oldParentId === newParentId && oldIndex === newIndex && newParentId !== null) {
    return tree;
  }
  if (oldParentId === null && newParentId === null && oldIndex === newIndex) {
    return tree;
  }

  // Remove node from old location
  const removeFromTree = (nodes) => {
    return nodes.filter(node => {
      if (node.id === nodeId) {
        return false;
      }
      if (node.children && node.children.length > 0) {
        node.children = removeFromTree(node.children);
      }
      return true;
    });
  };

  let updatedTree = removeFromTree(JSON.parse(JSON.stringify(tree))); // Deep clone

  // Adjust index if moving within same parent
  let adjustedIndex = newIndex;
  if (oldParentId === newParentId && oldParentId !== null) {
    if (oldIndex < newIndex) {
      adjustedIndex = newIndex - 1;
    }
  }

  // Add to new location
  if (!newParentId) {
    // Add as root node
    updatedTree.splice(adjustedIndex, 0, nodeToMove);
    return updatedTree;
  }

  const addToTree = (nodes) => {
    return nodes.map(node => {
      if (node.id === newParentId) {
        const newChildren = [...(node.children || [])];
        newChildren.splice(adjustedIndex, 0, nodeToMove);
        return {
          ...node,
          children: newChildren,
        };
      }
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: addToTree(node.children),
        };
      }
      return node;
    });
  };

  return addToTree(updatedTree);
};

/**
 * Duplicate a view node
 */
export const duplicateViewNode = (nodeId, tree) => {
  const duplicateInNodes = (nodes) => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        const duplicated = {
          ...node,
          id: generateViewId(node.type),
          children: duplicateChildren(node.children || []),
        };
        return duplicated;
      }
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: duplicateInNodes(node.children),
        };
      }
      return node;
    });
  };

  const duplicateChildren = (children) => {
    return children.map(child => ({
      ...child,
      id: generateViewId(child.type),
      children: duplicateChildren(child.children || []),
    }));
  };

  const result = duplicateInNodes(tree);
  // Find the duplicated node and add it after the original
  const originalIndex = tree.findIndex(n => n.id === nodeId);
  if (originalIndex !== -1) {
    const duplicated = result[originalIndex];
    result.splice(originalIndex + 1, 0, duplicated);
    result.splice(originalIndex, 1);
  }

  return result;
};

/**
 * Validate view tree structure
 */
export const validateViewTree = (tree) => {
  const errors = [];
  
  const validateNode = (node, path = '') => {
    if (!node.id) {
      errors.push(`Node missing ID at ${path}`);
    }
    if (!node.type) {
      errors.push(`Node missing type at ${path}`);
    }
    if (!node.properties) {
      errors.push(`Node missing properties at ${path}`);
    }
    if (!Array.isArray(node.children)) {
      errors.push(`Node children must be array at ${path}`);
    }
    
    const containerTypes = ['HStack', 'VStack', 'ZStack', 'List', 'ScrollView'];
    if (containerTypes.includes(node.type) && !node.children) {
      errors.push(`Container type ${node.type} must have children array at ${path}`);
    }

    if (node.children) {
      node.children.forEach((child, index) => {
        validateNode(child, `${path}.children[${index}]`);
      });
    }
  };

  if (!Array.isArray(tree)) {
    errors.push('View tree must be an array');
    return errors;
  }

  tree.forEach((node, index) => {
    validateNode(node, `root[${index}]`);
  });

  return errors;
};

/**
 * Check if a view type can have children
 */
export const canHaveChildren = (type) => {
  return ['HStack', 'VStack', 'ZStack', 'List', 'ScrollView'].includes(type);
};

/**
 * Get all available view types
 * @param {string} mode - 'card' or 'fullUI'. In 'card' mode, 'List' is excluded.
 */
export const getAvailableViewTypes = (mode = 'card') => {
  const allTypes = [
    { type: 'HStack', label: 'HStack', canHaveChildren: true },
    { type: 'VStack', label: 'VStack', canHaveChildren: true },
    { type: 'ZStack', label: 'ZStack', canHaveChildren: true },
    { type: 'Text', label: 'Text', canHaveChildren: false },
    { type: 'Button', label: 'Button', canHaveChildren: false },
    { type: 'Image', label: 'Image', canHaveChildren: false },
    { type: 'Spacer', label: 'Spacer', canHaveChildren: false },
    { type: 'Divider', label: 'Divider', canHaveChildren: false },
    { type: 'List', label: 'List', canHaveChildren: true },
    { type: 'ScrollView', label: 'ScrollView', canHaveChildren: true },
  ];
  
  // Filter out 'List' when in card mode
  if (mode === 'card') {
    return allTypes.filter(type => type.type !== 'List');
  }
  
  return allTypes;
};
